import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ExternalLink, Loader2, ShieldCheck, AlertCircle, RefreshCcw } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";

const fmt = (n) =>
  n == null ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Broker linking page — surfaces the user's real Interactive Brokers account
 * (positions, cash, account label) once the VPS-hosted IBKR sidecar is wired
 * up. Before linking is complete, renders an onboarding card with the steps.
 *
 * Read-only by design — order placement is a Phase 5 future-work item that
 * comes with a disclaimer flow + double-confirm modal.
 */
export default function Broker() {
  const [status, setStatus] = useState(null); // {linked, mode, account, read_only, reason}
  const [positions, setPositions] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const s = await api.get("/broker/status");
      setStatus(s.data);
      if (s.data.linked) {
        const [p, a] = await Promise.all([
          api.get("/broker/positions").catch((e) => ({ data: { positions: [] }, _err: e })),
          api.get("/broker/account").catch((e) => ({ data: { accounts: {} }, _err: e })),
        ]);
        setPositions(p.data.positions || []);
        setAccount(a.data.accounts || {});
        if (p._err || a._err) {
          setError("Some IBKR data failed to load — check the sidecar logs.");
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load broker status");
      setStatus({ linked: false, reason: "main_backend_error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="broker-page">
        <header className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
            Live brokerage account
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B] mt-1 flex items-center gap-3">
              Your broker
              <Briefcase className="w-7 h-7 text-[#547A8F]" strokeWidth={1.5} />
            </h1>
            <button
              onClick={refresh}
              data-testid="broker-refresh-btn"
              className="text-xs rounded-full px-3 py-1.5 border border-[#EAE6E1] bg-white hover:border-[#547A8F] transition-soft flex items-center gap-1.5 text-[#2A2F2B]"
            >
              <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.75} /> Refresh
            </button>
          </div>
          <p className="text-[#6B7068] mt-2 max-w-2xl">
            View your real Interactive Brokers portfolio alongside UpTick's AI insights, sentiment, and CAN SLIM scores. Currently <strong>read-only</strong> — order placement coming in a future release with a confirmation flow.
          </p>
        </header>

        {loading && !status && (
          <div className="flex items-center gap-2 text-[#6B7068]" data-testid="broker-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Checking your broker link...
          </div>
        )}

        {!loading && status && !status.linked && (
          <NotLinkedCard reason={status.reason} />
        )}

        {!loading && status && status.linked && (
          <LinkedView status={status} positions={positions} account={account} error={error} />
        )}
      </main>
    </div>
  );
}

function NotLinkedCard({ reason }) {
  const REASONS = {
    sidecar_env_unset: "The UpTick backend isn't pointed at your IBKR sidecar yet.",
    sidecar_unreachable: "Your IBKR sidecar isn't reachable. Check the VPS is online and the firewall allows the connection.",
    gateway_disconnected: "Sidecar is up, but IB Gateway lost its connection. Check `journalctl -u ibgateway -f` on the VPS.",
    main_backend_error: "Backend error reaching the broker proxy.",
  };
  return (
    <div className="rounded-2xl bg-white border border-[#EAE6E1] p-8 max-w-2xl" data-testid="broker-not-linked">
      <div className="w-12 h-12 rounded-full bg-[#547A8F]/10 flex items-center justify-center mb-4">
        <ShieldCheck className="w-6 h-6 text-[#547A8F]" strokeWidth={1.5} />
      </div>
      <h2 className="font-heading text-2xl text-[#2A2F2B] mb-2">Connect your Interactive Brokers account</h2>
      <p className="text-sm text-[#6B7068] mb-5 leading-relaxed">
        {REASONS[reason] || "Your broker isn't linked yet."} Once you complete the steps below your real positions + P&amp;L will appear here next to your paper portfolio.
      </p>
      <ol className="space-y-3 mb-6">
        {[
          { n: 1, h: "Open an IBKR Pro account", b: "Lite tier doesn't support API access. Pro signup takes 1–5 business days for approval." },
          { n: 2, h: "Enable a paper trading account + API access", b: "Free with any Pro account. Settings → Configuration → Paper Trading + API Settings." },
          { n: 3, h: "Set up the IBKR sidecar on your VPS", b: "Follow docs/IBKR_VPS_SETUP.md for the full deploy steps (IB Gateway + the UpTick sidecar)." },
          { n: 4, h: "Paste the sidecar URL + token into UpTick", b: "Once those env vars are set in /app/backend/.env, this page flips to your live portfolio view automatically." },
        ].map((step) => (
          <li key={step.n} className="flex gap-3" data-testid={`broker-step-${step.n}`}>
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#2A2F2B] text-white text-xs font-mono-ticker flex items-center justify-center">
              {step.n}
            </span>
            <div>
              <div className="text-sm font-semibold text-[#2A2F2B]">{step.h}</div>
              <div className="text-xs text-[#6B7068] mt-0.5">{step.b}</div>
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3">
        <a
          href="https://www.interactivebrokers.com/en/index.php?f=4715"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="broker-signup-link"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm transition-soft"
        >
          Open an IBKR account <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} />
        </a>
        <Link
          to="/paper"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#EAE6E1] hover:border-[#547A8F] text-[#2A2F2B] text-sm transition-soft"
        >
          Stick with paper trading for now
        </Link>
      </div>
    </div>
  );
}

function LinkedView({ status, positions, account, error }) {
  // Pull a few headline numbers out of the IBKR account-summary dump
  const acct = status.account ? account?.[status.account] : null;
  const nlv = acct?.NetLiquidation?.value;
  const cash = acct?.TotalCashValue?.value;
  return (
    <>
      <div
        className="rounded-2xl bg-white border border-[#EAE6E1] p-5 mb-6 flex items-center gap-4 flex-wrap"
        data-testid="broker-status-strip"
      >
        <div
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: status.read_only ? "#5C8D73" : "#D18E63" }}
        />
        <div className="font-mono-ticker text-sm text-[#2A2F2B]">
          {status.account || "—"}
        </div>
        <div className="text-xs text-[#6B7068]">
          {status.mode === "paper" ? "Paper mode" : "Live"}
          {status.read_only && " · read-only"}
        </div>
      </div>

      {error && (
        <div
          className="rounded-2xl bg-[#C66B59]/10 border border-[#C66B59]/30 p-4 text-sm text-[#C66B59] mb-4 flex items-center gap-2"
          data-testid="broker-error"
        >
          <AlertCircle className="w-4 h-4" strokeWidth={1.5} /> {error}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <SummaryTile label="Net liquidation value" value={nlv ? `$${fmt(nlv)}` : "—"} testid="broker-nlv" />
        <SummaryTile label="Cash" value={cash ? `$${fmt(cash)}` : "—"} testid="broker-cash" />
        <SummaryTile label="Positions" value={positions.length} testid="broker-position-count" />
      </div>

      <section data-testid="broker-positions-section">
        <h2 className="font-heading text-2xl text-[#2A2F2B] mb-4">Open positions</h2>
        {positions.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 text-sm text-[#6B7068]">
            No open positions in your IBKR account.
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden">
            <div className="hidden md:grid grid-cols-[120px_1fr_120px_120px_120px] gap-4 px-6 py-3 border-b border-[#EAE6E1] text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7068]">
              <div>Symbol</div>
              <div>Asset / Exch</div>
              <div className="text-right">Qty</div>
              <div className="text-right">Avg cost</div>
              <div className="text-right">Cost basis</div>
            </div>
            {positions.map((p) => {
              const cost = p.quantity * p.avg_cost;
              return (
                <Link
                  key={`${p.symbol}-${p.exchange}-${p.account}`}
                  to={`/asset/stock/${p.symbol}`}
                  data-testid={`broker-position-${p.symbol}`}
                  className="grid grid-cols-[1fr_auto] md:grid-cols-[120px_1fr_120px_120px_120px] gap-4 items-center px-6 py-3 border-b border-[#EAE6E1] last:border-0 hover:bg-[#F9F8F6] transition-soft"
                >
                  <div className="font-mono-ticker font-semibold text-[#2A2F2B]">{p.symbol}</div>
                  <div className="hidden md:block text-xs text-[#6B7068]">
                    {p.asset_type.toUpperCase()} · {p.exchange} · {p.currency}
                  </div>
                  <div className="hidden md:block text-right font-mono-ticker text-sm text-[#2A2F2B]">{p.quantity.toFixed(4)}</div>
                  <div className="hidden md:block text-right font-mono-ticker text-sm text-[#6B7068]">${fmt(p.avg_cost)}</div>
                  <div className="text-right font-mono-ticker text-sm text-[#2A2F2B]">${fmt(cost)}</div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#EAE6E1] p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7068] mb-1.5">{label}</div>
      <div className="font-mono-ticker text-2xl text-[#2A2F2B]">{value}</div>
    </div>
  );
}
